import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiPrinter, FiEdit2, FiArrowLeft } from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import feeService from "../../services/feeService";
import PrintInstructions from "../../components/common/PrintInstructions";
import CollegePrintHeader from "../../components/common/CollegePrintHeader";
import { numberToWordsRupees } from "../../utils/numberToWords";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

export default function FeeReceiptPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { role, user } = useApp();
    const feeBasePath = role === "clerk" ? "/clerk/fees" : role === "accountant" ? "/accountant/fees" : "/fees";
    const [receipt, setReceipt] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await feeService.getById(id);
                setReceipt(res.data);
                if (res.data?.student?._id) {
                    try {
                        const sumRes = await feeService.getStudentSummary(res.data.student._id);
                        setSummary(sumRes.data || sumRes);
                    } catch {
                        // ignore summary fetch error
                    }
                }
            } catch (err) {
                toast.error(err.message);
                navigate(`${feeBasePath}/receipts`);
            } finally {
                setLoading(false);
            }
        })();
    }, [id, navigate, feeBasePath]);

    if (loading) return <div className="loading-center"><div className="spinner" />Loading receipt…</div>;
    if (!receipt) return null;

    const s = receipt.student || {};
    const ay = receipt.academicYear;
    const college = receipt.college || s.college || user?.linkedCollege || {};

    const hasBreakdown = Array.isArray(receipt.breakdown) && receipt.breakdown.length > 0;
    const amountInWords = numberToWordsRupees(receipt.amountPaid);

    const feesDecided = Number(summary?.feesDecided ?? s.feesDecided ?? 0);
    const totalPaid = Number(summary?.totalPaid ?? receipt.totalPaid ?? receipt.amountPaid ?? 0);
    const balanceFees = summary?.pendingBalance !== undefined
        ? Number(summary.pendingBalance)
        : (receipt.balanceFees !== undefined ? Number(receipt.balanceFees) : Math.max(0, feesDecided - totalPaid));

    return (
        <div>
            {/* Toolbar — hidden on print */}
            <div className="no-print" style={{
                display: "flex",
                gap: "0.75rem",
                alignItems: "center",
                marginBottom: "1rem",
                flexWrap: "wrap"
            }}>
                <button className="btn btn-outline" onClick={() => navigate(-1)}>
                    <FiArrowLeft size={14} /> Back
                </button>
                <h1 className="page-title" style={{ flex: 1, margin: 0, fontSize: 18 }}>
                    Fee Receipt — <span style={{ fontFamily: "monospace", color: "var(--primary)" }}>{receipt.receiptNo}</span>
                </h1>
                <button className="btn btn-outline" onClick={() => navigate(`${feeBasePath}/receipts/${id}/edit`)}>
                    <FiEdit2 size={14} /> Edit
                </button>
                <button className="btn btn-success" onClick={() => window.print()}>
                    <FiPrinter size={14} /> Print Receipt
                </button>
            </div>

            <div className="no-print" style={{ marginBottom: "1rem" }}>
                <PrintInstructions />
            </div>

            {/* ── PRINTABLE RECEIPT CONTAINER ─────────────────────────────────── */}
            <div
                className="print-area fee-receipt-document"
                style={{
                    width: "5.8in",
                    minHeight: "7.8in",
                    background: "#ffffff",
                    padding: "0.35in 0.4in",
                    boxSizing: "border-box",
                    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
                    fontSize: "12.5px",
                    color: "#0f172a",
                    border: "1.5px solid #0f172a",
                    borderRadius: "4px",
                    margin: "0 auto",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                    position: "relative",
                }}
            >
                <style>{`
                    @media print {
                        @page {
                            size: 5.8in 8.2in;
                            margin: 0.1in;
                        }
                        body {
                            background: #fff !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        .fee-receipt-document {
                            border: 1.5px solid #0f172a !important;
                            box-shadow: none !important;
                            width: 100% !important;
                            max-width: 100% !important;
                            padding: 0.3in 0.35in !important;
                        }
                    }
                `}</style>

                {/* ── Dynamic College Header ── */}
                <CollegePrintHeader
                    college={college}
                    layout="banner"
                    logoSize={58}
                    showBorders={true}
                />

                {/* ── Title & Meta Bar ── */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px",
                    padding: "4px 0",
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        <span style={{
                            background: "#0f172a",
                            color: "#fff",
                            fontWeight: 900,
                            fontSize: "12px",
                            padding: "2px 8px",
                            borderRadius: "3px",
                            letterSpacing: "0.6px"
                        }}>
                            FEE RECEIPT
                        </span>
                        <span style={{ fontSize: "12px", color: "#334155", fontWeight: 700 }}>
                            Receipt No: <strong style={{ fontFamily: "monospace", color: "#1e3a8a", fontSize: "13px" }}>{receipt.receiptNo}</strong>
                        </span>
                        <span style={{ color: "#cbd5e1" }}>•</span>
                        <span style={{ fontSize: "11px", fontWeight: 700, color: "#475569" }}>
                            AY: {ay?.label || "—"}
                        </span>
                    </div>

                    <div style={{ fontSize: "11px", color: "#334155" }}>
                        Date: <strong style={{ color: "#0f172a" }}>{receipt.paymentDate}</strong>
                    </div>
                </div>

                {/* ── Student Profile Grid ── */}
                <div style={{
                    background: "#f8fafc",
                    border: "1px solid #cbd5e1",
                    borderRadius: "4px",
                    padding: "8px 12px",
                    marginBottom: "12px"
                }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: "4px 14px", fontSize: "11.5px" }}>
                        <div>
                            <span style={{ color: "#64748b" }}>Student Name: </span>
                            <strong style={{ color: "#0f172a" }}>{s.fullName || "—"}</strong>
                        </div>
                        <div>
                            <span style={{ color: "#64748b" }}>Enrollment No: </span>
                            <strong style={{ fontFamily: "monospace", color: "#0f172a" }}>{s.enrollmentNo || "—"}</strong>
                        </div>
                        <div>
                            <span style={{ color: "#64748b" }}>Course / Year: </span>
                            <strong>{s.courseAdmitted || s.course || "—"} {s.courseYear ? `· ${s.courseYear}` : ""}</strong>
                        </div>
                        <div>
                            <span style={{ color: "#64748b" }}>Category: </span>
                            <strong>{s.admissionCategory || "Open"}</strong>
                        </div>
                        {s.fatherName && (
                            <div>
                                <span style={{ color: "#64748b" }}>Father's Name: </span>
                                <strong>{s.fatherName}</strong>
                            </div>
                        )}
                        {s.mobile && (
                            <div>
                                <span style={{ color: "#64748b" }}>Contact: </span>
                                <strong>{s.mobile}</strong>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Particulars & Breakdown Table ── */}
                <table style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginBottom: "8px",
                    fontSize: "11.5px"
                }}>
                    <thead>
                        <tr style={{ background: "#0f172a", color: "#ffffff" }}>
                            <th style={{ padding: "6px 8px", textAlign: "left", borderRadius: "3px 0 0 0", fontWeight: 700 }}>
                                Particulars / Fee Description
                            </th>
                            <th style={{ padding: "6px 8px", textAlign: "right", borderRadius: "0 3px 0 0", width: "110px", fontWeight: 700 }}>
                                Amount
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {hasBreakdown ? (
                            receipt.breakdown.map((item, idx) => (
                                <tr key={idx} style={{ borderBottom: "1px solid #e2e8f0" }}>
                                    <td style={{ padding: "6px 8px" }}>
                                        <div style={{ fontWeight: 600 }}>{item.head}</div>
                                        {item.category && item.category !== "Academic" && (
                                            <span style={{ fontSize: "9.5px", color: "#64748b" }}>({item.category})</span>
                                        )}
                                    </td>
                                    <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 600 }}>
                                        {inr(item.amount)}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                                <td style={{ padding: "7px 8px" }}>
                                    <div style={{ fontWeight: 700 }}>
                                        {receipt.installmentLabel || `Installment #${receipt.installmentNo}`}
                                    </div>
                                    {receipt.remarks && (
                                        <div style={{ fontSize: "10px", color: "#64748b", marginTop: 2 }}>
                                            Remarks: {receipt.remarks}
                                        </div>
                                    )}
                                </td>
                                <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 700 }}>
                                    {inr(receipt.amountPaid)}
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr style={{ background: "#f0fdf4", borderTop: "2px solid #0f172a" }}>
                            <td style={{ padding: "7px 8px", fontWeight: 800, fontSize: "12px", color: "#14532d" }}>
                                Total Amount Paid
                            </td>
                            <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 900, fontSize: "14px", color: "#15803d" }}>
                                {inr(receipt.amountPaid)}
                            </td>
                        </tr>
                    </tfoot>
                </table>

                {/* ── Amount in Words ── */}
                <div style={{
                    fontSize: "11px",
                    background: "#f1f5f9",
                    padding: "5px 10px",
                    borderRadius: "3px",
                    marginBottom: "10px",
                    borderLeft: "3px solid #0f172a",
                }}>
                    <span style={{ color: "#475569", fontWeight: 600 }}>Amount in Words: </span>
                    <strong style={{ color: "#0f172a" }}>{amountInWords}</strong>
                </div>

                {/* ── Payment Details Bar ── */}
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "4px 14px",
                    fontSize: "11px",
                    background: "#fafafa",
                    padding: "6px 10px",
                    borderRadius: "4px",
                    border: "1px solid #e5e7eb",
                    marginBottom: "10px"
                }}>
                    <div>
                        <span style={{ color: "#64748b" }}>Payment Mode: </span>
                        <strong>{receipt.paymentMode}</strong>
                    </div>
                    {receipt.transactionRef && (
                        <div>
                            <span style={{ color: "#64748b" }}>Txn / UTR Ref: </span>
                            <strong style={{ fontFamily: "monospace" }}>{receipt.transactionRef}</strong>
                        </div>
                    )}
                    {receipt.chequeNo && (
                        <div>
                            <span style={{ color: "#64748b" }}>Cheque / DD No: </span>
                            <strong>{receipt.chequeNo}</strong>
                        </div>
                    )}
                    {receipt.bankName && (
                        <div>
                            <span style={{ color: "#64748b" }}>Bank Name: </span>
                            <strong>{receipt.bankName}</strong>
                        </div>
                    )}
                </div>

                {/* ── Fee Summary & Balance Ledger ── */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: "11px",
                    color: "#334155",
                    background: "#f8fafc",
                    padding: "7px 12px",
                    borderRadius: "4px",
                    marginBottom: "18px",
                    border: "1px solid #cbd5e1",
                    flexWrap: "wrap",
                    gap: "8px"
                }}>
                    <div>
                        <span style={{ color: "#64748b" }}>Fees Decided: </span>
                        <strong style={{ color: "#0f172a" }}>{inr(feesDecided)}</strong>
                    </div>
                    <div>
                        <span style={{ color: "#64748b" }}>Total Paid: </span>
                        <strong style={{ color: "#15803d" }}>{inr(totalPaid)}</strong>
                    </div>
                    <div style={{
                        background: balanceFees > 0 ? "#fef2f2" : "#f0fdf4",
                        padding: "2px 8px",
                        borderRadius: "3px",
                        border: `1px solid ${balanceFees > 0 ? "#fca5a5" : "#86efac"}`
                    }}>
                        <span style={{ color: balanceFees > 0 ? "#991b1b" : "#166534", fontWeight: 700 }}>
                            Balance Fees:{" "}
                        </span>
                        <strong style={{ color: balanceFees > 0 ? "#dc2626" : "#15803d", fontSize: "11.5px" }}>
                            {inr(balanceFees)}
                        </strong>
                    </div>
                    {s.scholarshipAmountBalance > 0 && (
                        <div>
                            <span style={{ color: "#64748b" }}>Scholarship Dues: </span>
                            <strong style={{ color: "#0f172a" }}>{inr(s.scholarshipAmountBalance)}</strong>
                        </div>
                    )}
                </div>

                {/* ── 3-Zone Signature Block ── */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    paddingTop: "14px",
                    marginTop: "auto"
                }}>
                    {[
                        { label: "Student / Depositor" },
                        { label: "Accounts / Cashier" },
                        { label: `Principal / Authorized Signatory` },
                    ].map(({ label }) => (
                        <div key={label} style={{ textAlign: "center", fontSize: "10px", width: "30%" }}>
                            <div style={{ height: 28 }} />
                            <div style={{
                                borderTop: "1.2px solid #0f172a",
                                paddingTop: "4px",
                                whiteSpace: "pre-line",
                                fontWeight: 700,
                                color: "#1e293b",
                                lineHeight: 1.2
                            }}>
                                {label}
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Security / Computer Generated Footer ── */}
                <div style={{
                    marginTop: "12px",
                    fontSize: "8.5px",
                    color: "#94a3b8",
                    textAlign: "center",
                    borderTop: "1px solid #e2e8f0",
                    paddingTop: "6px"
                }}>
                    This is a computer-generated official receipt. Valid subject to realization of Cheque/DD/Electronic transfer.
                    {receipt.createdAt && ` · Generated on ${new Date(receipt.createdAt).toLocaleString("en-IN")}`}
                </div>
            </div>
        </div>
    );
}